/**
 * A club's colours, applied to the shop.
 *
 * The storefront is the club's shop, not Pitchbase's. A supporter arriving
 * from their club's own website should see their club's colours, so the accent
 * — buttons, section headings, the on-sale badge — comes from the club record
 * rather than the product's green.
 *
 * Text on the accent is CHOSEN, not assumed. Claret takes white; a club
 * playing in yellow or sky blue does not, and white-on-yellow is unreadable
 * rather than merely off-brand. So the label colour is picked from the
 * accent's luminance.
 */

/** #abc and #aabbcc, with or without the hash. Anything else is unusable. */
const parse = (colour?: string | null) => {
	if (!colour) return null;
	let hex = String(colour).trim().replace(/^#/, "");
	if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
	if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
	return {
		b: parseInt(hex.slice(4, 6), 16),
		g: parseInt(hex.slice(2, 4), 16),
		r: parseInt(hex.slice(0, 2), 16)
	};
};

/** Relative luminance, per WCAG. */
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
	const channel = (v: number) => {
		const s = v / 255;
		return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
	};
	return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const shift = ({ r, g, b }: { r: number; g: number; b: number }, by: number) => {
	const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
	return `rgb(${clamp(r + by)}, ${clamp(g + by)}, ${clamp(b + by)})`;
};

/**
 * CSS custom properties for a club's shop, or an empty object when the club
 * has no usable colour — in which case the stylesheet's own defaults stand.
 */
export function clubTheme(club: any): React.CSSProperties {
	const rgb = parse(club?.primaryColour);
	if (!rgb) return {};

	const light = luminance(rgb);

	// A very dark club colour disappears against the shop's dark background,
	// so it gets lifted rather than rendered as an invisible button.
	const accent = light < 0.06 ? shift(rgb, 55) : `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`;

	return {
		"--shop-accent": accent,
		"--shop-accent-hover": shift(rgb, light > 0.4 ? -22 : 22),
		"--shop-accent-soft": `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.18)`,
		// Headings sit on the dark page, not on the accent, so a dark club
		// colour needs lifting there too or the section titles vanish.
		"--shop-accent-text": light < 0.14 ? shift(rgb, 90) : accent,
		"--shop-on-accent": light > 0.45 ? "#111827" : "#ffffff"
	} as React.CSSProperties;
}
